// Log Book Почеток/Крај timestamps (RN productivity, OSProductivityTable / LogBookDateTimeCell).
// Stored value is ALWAYS the full local `DD.MM.YYYY HH:mm`. Pure helpers only — no React.

const pad2 = (value: number): string => String(value).padStart(2, "0");

export const formatDateTime = (date: Date): string =>
  `${pad2(date.getDate())}.${pad2(date.getMonth() + 1)}.${date.getFullYear()} ${pad2(date.getHours())}:${pad2(date.getMinutes())}`;

const FULL_DATE_TIME_RE = /^(\d{1,2})\.(\d{1,2})\.(\d{4})\s+(\d{1,2}):(\d{2})$/;

const isValidTime = (hours: number, minutes: number): boolean =>
  hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59;

// Strict `DD.MM.YYYY HH:mm` → local Date; null for anything else (incl. 31.02., 25:00).
export const parseFullDateTime = (value: unknown): Date | null => {
  const match = String(value ?? "").trim().match(FULL_DATE_TIME_RE);
  if (!match) return null;
  const [, rawDay, rawMonth, rawYear, rawHours, rawMinutes] = match;
  const day = Number(rawDay);
  const month = Number(rawMonth);
  const year = Number(rawYear);
  const hours = Number(rawHours);
  const minutes = Number(rawMinutes);
  const date = new Date(year, month - 1, day, hours, minutes, 0, 0);
  const roundTrips =
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day &&
    date.getHours() === hours &&
    date.getMinutes() === minutes;
  return roundTrips ? date : null;
};

// Reader for STORED values (durations, aggregate rows, the picker's `selected`).
// Legacy tolerance: a bare `HH:mm` reads as today so odd old data still displays; typed
// entries never come through here — they go through resolveTypedDateTime.
export const parseDateTime = (value: unknown): Date | null => {
  const full = parseFullDateTime(value);
  if (full) return full;
  const timeMatch = String(value ?? "").trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!timeMatch) return null;
  const hours = Number(timeMatch[1]);
  const minutes = Number(timeMatch[2]);
  if (!isValidTime(hours, minutes)) return null;
  const today = new Date();
  return new Date(today.getFullYear(), today.getMonth(), today.getDate(), hours, minutes, 0, 0);
};

// A time typed without a date, in the forms an operator actually types: `15:00`, `15.00`,
// `15,00`, `1500`, `930`, `15` (= 15:00). Invalid hours/minutes or any other text → null.
export const parseTimeOnly = (text: string): { hours: number; minutes: number } | null => {
  const trimmed = String(text ?? "").trim();
  const match =
    trimmed.match(/^(\d{1,2})[:.,](\d{2})$/) ||
    trimmed.match(/^(\d{1,2})(\d{2})$/) ||
    trimmed.match(/^(\d{1,2})$/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = match[2] === undefined ? 0 : Number(match[2]);
  return isValidTime(hours, minutes) ? { hours, minutes } : null;
};

// What a typed cell text means (Enter / leaving the cell):
//   - a full `DD.MM.YYYY HH:mm` → that moment;
//   - a time only → that time on the row's Почеток монтажа date (`baseDate`), else on the
//     date already in this cell (`currentValue`), else today. Every Log Book time after
//     Почеток монтажа lies after it, so a time that would land BEFORE it (night shift: start
//     22:00, end typed „02:00") goes to the next day. Not for the Почеток монтажа cell itself
//     (its own value is the baseDate);
//   - anything else (incl. partial typing like `15.03.2026 1`) → null, nothing is written.
export const resolveTypedDateTime = (
  typed: string,
  baseDate?: string,
  currentValue?: string,
): Date | null => {
  const text = String(typed ?? "").trim();
  if (!text) return null;
  if (FULL_DATE_TIME_RE.test(text)) return parseFullDateTime(text);
  const time = parseTimeOnly(text);
  if (!time) return null;
  const base = parseFullDateTime(baseDate);
  const day = base ?? parseFullDateTime(currentValue) ?? new Date();
  const result = new Date(day.getFullYear(), day.getMonth(), day.getDate(), time.hours, time.minutes, 0, 0);
  const isBaseCell = !!baseDate && baseDate === currentValue;
  if (base && !isBaseCell && result < base) result.setDate(result.getDate() + 1);
  return result;
};

// Whole days from baseDate's date to value's date (+1 = the day after Почеток монтажа).
// null when either side is not a full stored value. Calendar days, DST-safe.
export const dayDifference = (value: string | undefined, baseDate: string | undefined): number | null => {
  const cell = parseFullDateTime(value);
  const base = parseFullDateTime(baseDate);
  if (!cell || !base) return null;
  const cellDay = Date.UTC(cell.getFullYear(), cell.getMonth(), cell.getDate());
  const baseDay = Date.UTC(base.getFullYear(), base.getMonth(), base.getDate());
  return Math.round((cellDay - baseDay) / 86400000);
};

// `(+1)` / `(−2)` suffix shown after HH:mm in the compact cell. Empty for the same day.
export const dayDifferenceLabel = (diff: number | null): string =>
  diff ? `(${diff > 0 ? "+" : "−"}${Math.abs(diff)})` : "";
